import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { OpenInDriveButton } from '@/components/drive/OpenInDriveButton';
import { ArrowLeft, MapPin, Phone, Mail, Building2, ClipboardCheck, Home, Key, Send, Copy, CheckCircle, ExternalLink, RefreshCw, Plus, Edit2, Trash2, Image as ImageIcon, AlertTriangle, X } from 'lucide-react';
import Combobox from '@/components/Combobox';
import ClientFormModal from '@/components/ClientFormModal';
import { AppraisalStartButtons } from '@/components/AppraisalStartButtons';
import RoofDiagramPicker from '@/components/RoofDiagramPicker';
import AttachmentsTab from '@/components/AttachmentsTab';
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog';
import IdChip from '@/components/IdChip';

const PORTAL_URL = window.location.origin + '/portal';

export default function ClientDetailPage() {
  const { api } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [copied, setCopied] = useState(false);
  // Inline Roof Diagram picker: tracks the property id currently being
  // edited so we can render <RoofDiagramPicker> for it on demand.
  const [roofDiagramPropId, setRoofDiagramPropId] = useState(null);
  const [cityOptions, setCityOptions] = useState([]);
  const [suburbOptions, setSuburbOptions] = useState([]);
  const [suburbMeta, setSuburbMeta] = useState([]);
  const [showEditClient, setShowEditClient] = useState(false);
  // Iter179 — inline "Add Property" form attached to the Properties
  // section header (separate from the New-Appraisal inline form so
  // the operator can grow a client's property list without going
  // via Appraisals).
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [addPropForm, setAddPropForm] = useState({ property_address_num: '', property_address_street: '', property_address_suburb: '', property_address_city: '' });
  const [savingAddProp, setSavingAddProp] = useState(false);
  // Iter202 — duplicate-property guard state (see saveAddProperty).
  // addPropDupe = the matching existing property (renders the confirm
  // dialog when non-null). addPropDupeAcked = the _id the operator has
  // chosen to "Create anyway" — bumps the next save through the dupe
  // check.
  const [addPropDupe, setAddPropDupe] = useState(null);
  // Iter178 — universal Soft/Hard Delete dialog targets. When set,
  // the modal renders for that target; null = closed.
  // delTarget shape: { kind: 'client'|'property'|'appraisal', id, label }
  const [delTarget, setDelTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${id}`);
      setClient(data);
    } catch { navigate('/clients'); }
    setLoading(false);
  }, [api, id, navigate]);

  useEffect(() => { load(); }, [load]);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('add_property') === '1') {
      setShowAddProperty(true);
      searchParams.delete('add_property');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    api.get('/item-descriptions/by-category').catch(() => {});
  }, [api]);

  // Load address dropdowns when the New Appraisal modal OR the
  // inline Add-Property form opens (so the suburb/city pickers are
  // freshly populated either way — Iter179).
  useEffect(() => {
    if (!showAddProperty) return;
    Promise.all([
      api.get('/dropdowns/address_cities').catch(() => ({ data: [] })),
      api.get('/dropdowns/address_suburbs').catch(() => ({ data: [] })),
      api.get('/dropdowns/address_suburbs_meta').catch(() => ({ data: [] })),
    ]).then(([c, s, sm]) => {
      setCityOptions(Array.isArray(c.data) ? c.data : []);
      setSuburbOptions(Array.isArray(s.data) ? s.data : []);
      setSuburbMeta(Array.isArray(sm.data) ? sm.data : []);
    });
  }, [api, showAddProperty]);

  // Iter179 — separate handler used by the Properties section's
  // inline Add-Property form (different from saveNewProperty which is
  // tied to the New-Appraisal modal flow).
  // Iter202 — pre-save duplicate guard for the inline "Add Property"
  // form. The walkthrough revealed that operators were unknowingly
  // creating doubled properties (same address, same client) because:
  //   1. The New Client save auto-creates a property from the address.
  //   2. On the detail page, "+ Add Property" → "Use Client Address"
  //      builds the same payload and silently creates a 2nd identical
  //      property.
  // We normalize each candidate's num + street + suburb (trimmed and
  // lower-cased) and compare against every property already on file
  // for this client. Match → require explicit "Create anyway" confirm.
  const normalizeAddr = (p) => [
    (p.property_address_num || '').toString().trim().toLowerCase(),
    (p.property_address_street || '').trim().toLowerCase().replace(/\s+/g, ' '),
    (p.property_address_suburb || '').trim().toLowerCase().replace(/\s+/g, ' '),
  ].join('|');

  const findExistingPropertyMatch = (candidate) => {
    const norm = normalizeAddr(candidate);
    if (!norm.replace(/\|/g, '')) return null; // empty candidate, skip
    return (client?.properties || []).find(p => normalizeAddr(p) === norm) || null;
  };

  const saveAddProperty = async () => {
    if (!addPropForm.property_address_street.trim()) return;
    // Dupe guard — block unless the operator has already acknowledged
    // (`addPropDupeAcked` is bumped to the matching property _id by
    // the confirm dialog).
    const dupe = findExistingPropertyMatch(addPropForm);
    if (dupe) {
      setAddPropDupe(dupe);
      return;
    }
    setSavingAddProp(true);
    try {
      await api.post('/properties', { ...addPropForm, client_id: id });
      await load();
      setShowAddProperty(false);
      setAddPropForm({ property_address_num: '', property_address_street: '', property_address_suburb: '', property_address_city: '' });
      setAddPropDupe(null);
    } catch (e) {
      if (e?.response?.data?.code !== 'duplicate_address') alert(e?.response?.data?.detail || 'Failed to create property');
    } finally {
      setSavingAddProp(false);
    }
  };

  const deleteAppraisal = async (appId) => {
    // Iter177 — kept for backwards-compatibility callers but the
    // detail page now opens DeleteConfirmDialog instead. Routes the
    // raw call through the soft-delete endpoint so legacy hits still
    // honour the new convention.
    try {
      await api.delete(`/appraisals/${appId}?mode=soft`);
      load();
    } catch (e) { alert('Failed to delete appraisal'); }
  };

  const deleteProperty = async (propId) => {
    // Iter178 — kept for backwards-compatibility callers but the
    // detail page now opens DeleteConfirmDialog instead.
    try {
      await api.delete(`/properties/${propId}?mode=soft`);
      load();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed to delete property'); }
  };

  const generateAccess = async () => {
    setGenerating(true);
    try {
      await api.post('/portal/generate-access', { client_id: id });
      load();
    } catch (e) { console.error(e); }
    setGenerating(false);
  };

  const sendAccessEmail = async () => {
    setSending(true);
    setSendResult(null);
    try {
      const { data } = await api.post('/portal/send-access-email', { client_id: id, portal_url: PORTAL_URL });
      setSendResult(data);
    } catch (e) {
      setSendResult({ email_result: { status: 'failed', message: e.response?.data?.detail || 'Failed' } });
    }
    setSending(false);
  };

  const copyCode = () => {
    if (client?.portal_access?.access_code) {
      navigator.clipboard.writeText(client.portal_access.access_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyPortalLink = () => {
    navigator.clipboard.writeText(PORTAL_URL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Loading...</div>;
  if (!client) return null;

  const hasPortalAccess = !!client.portal_access;

  return (
    <div data-testid="client-detail-page" className="space-y-6">
      <button onClick={() => navigate('/clients')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors">
        <ArrowLeft size={16} /> Back to Clients
      </button>
      {/* Client Header */}
      <div className="bg-white border border-[#E2E2DF] rounded-md p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#D35400]/10 flex items-center justify-center">
              <span className="text-xl font-bold text-[#D35400]">{(client.name_first || '?')[0]}{(client.name_last || '?')[0]}</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">{client.name_first} {client.name_last}</h1>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mt-0.5 inline-flex items-center gap-1.5">Client {(client.client_no || client.access_id) ? (
                <IdChip type="client" value={client.client_no || client.access_id} size="md" testId="client-header-id-chip" />
              ) : <span data-testid="client-header-id-missing">ID —</span>}</p>
              {client.is_company && client.company_name && <p className="text-sm text-slate-500 flex items-center gap-1 mt-1"><Building2 size={14} />{client.company_name}{client.company_type ? ` · ${client.company_type}` : ''}</p>}
            </div>
            {/* Lifetime Value Stats */}
            <div className="ml-auto flex items-center gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#D35400] font-mono">{client.job_count || 0}</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Jobs</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-700 font-mono">${(client.lifetime_value || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Lifetime Value</p>
              </div>
              <OpenInDriveButton entity="client" id={id} />
              <a data-testid="client-update-resend-btn" href={`/update-resend?client=${id}`} className="inline-flex items-center text-xs px-3 py-1.5 rounded-md border border-[#D35400]/40 text-[#D35400] font-semibold hover:bg-orange-50">Update & Resend</a>
              <button data-testid="edit-client-btn" onClick={() => setShowEditClient(true)} className="flex items-center gap-1.5 text-sm font-semibold text-[#D35400] border border-[#D35400] px-3 py-1.5 rounded-md hover:bg-[#FFF7ED] transition-colors">
                <Edit2 size={14} /> Edit
              </button>
              {/* Iter178 — Delete Client (soft / hard with safety
                  guards against active appraisals + properties). */}
              <button
                data-testid="delete-client-btn"
                onClick={() => setDelTarget({
                  kind: 'client',
                  id,
                  label: client.is_company && client.company_name
                    ? client.company_name
                    : `${client.name_first || ''} ${client.name_last || ''}`.trim() || `Client ${client.client_no || ''}`,
                })}
                className="flex items-center gap-1.5 text-sm font-semibold text-rose-600 border border-rose-300 px-3 py-1.5 rounded-md hover:bg-rose-50 transition-colors"
                title="Delete this client (soft or hard, with dependent guard)"
              >
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          {client.email && <div className="flex items-center gap-2 text-sm text-slate-600"><Mail size={14} className="text-slate-400" />{client.email}</div>}
          {client.phone_mobile && <div className="flex items-center gap-2 text-sm text-slate-600"><Phone size={14} className="text-slate-400" />{client.phone_mobile}</div>}
          {client.phone_home && <div className="flex items-center gap-2 text-sm text-slate-600"><Phone size={14} className="text-slate-400" />{client.phone_home} (Home)</div>}
          {client.address_street && <div className="flex items-center gap-2 text-sm text-slate-600"><MapPin size={14} className="text-slate-400" />{client.address_num} {client.address_street}, {client.address_suburb}, {client.address_city} {client.address_postcode}</div>}
          {client.client_referral && <div className="text-sm text-slate-600"><span className="text-xs font-bold uppercase tracking-wider text-slate-500">Referral: </span>{client.client_referral}</div>}
        </div>
        {client.client_comments && <p className="mt-4 text-sm text-slate-600 bg-slate-50 p-3 rounded">{client.client_comments}</p>}
      </div>

      {/* Portal Access Card */}
      <div data-testid="portal-access-card" className="bg-white border border-[#E2E2DF] rounded-md p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800 font-[Manrope] flex items-center gap-2">
            <Key size={18} className="text-purple-600" /> Client Portal Access
          </h3>
          {hasPortalAccess && (
            <span className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-green-50 text-green-700">
              <CheckCircle size={12} /> Active
            </span>
          )}
        </div>

        {hasPortalAccess ? (
          <div className="space-y-4">
            {/* Access Details */}
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</span>
                  <p className="text-sm font-medium text-slate-800 mt-1">{client.email || 'No email'}</p>
                </div>
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Access Code</span>
                  <div className="flex items-center gap-2 mt-1">
                    <code data-testid="portal-access-code" className="text-sm font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded font-mono tracking-wider">{client.portal_access.access_code}</code>
                    <button data-testid="copy-access-code" onClick={copyCode} className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors" title="Copy code">
                      {copied ? <CheckCircle size={14} className="text-green-500" /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Portal URL</span>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-slate-600 truncate">{PORTAL_URL}</span>
                    <button onClick={copyPortalLink} className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600" title="Copy link"><Copy size={14} /></button>
                    <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-blue-600" title="Open portal"><ExternalLink size={14} /></a>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <button data-testid="send-access-email-button" onClick={sendAccessEmail} disabled={sending || !client.email} className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-sm font-semibold disabled:opacity-50 transition-all">
                <Send size={14} /> {sending ? 'Sending...' : 'Email Access Code to Client'}
              </button>
              <button data-testid="regenerate-code-button" onClick={generateAccess} disabled={generating} className="flex items-center gap-2 px-4 py-2 border border-[#E2E2DF] rounded-md text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                <RefreshCw size={14} /> Regenerate Code
              </button>
            </div>

            {/* Send Result */}
            {sendResult && (
              <div className={`p-3 rounded-md text-sm ${sendResult.email_result?.status === 'sent' ? 'bg-green-50 text-green-700 border border-green-200' : sendResult.email_result?.status === 'logged' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="send-result">
                <p className="font-semibold">
                  {sendResult.email_result?.status === 'sent' ? 'Email sent!' : sendResult.email_result?.status === 'logged' ? 'Email logged (SMTP not configured)' : 'Send failed'}
                </p>
                <p className="text-xs mt-1">{sendResult.email_result?.message}</p>
                {sendResult.email_result?.status === 'logged' && (
                  <p className="text-xs mt-2 font-medium">To enable sending, go to <button onClick={() => navigate('/settings')} className="underline font-bold">Settings &gt; Email (SMTP)</button> and configure your SMTP credentials.</p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6">
            <Key size={32} className="mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-slate-500 mb-4">This client doesn't have portal access yet. Generate an access code so they can view appraisals, download quotes, and approve work online.</p>
            <button data-testid="generate-portal-access" onClick={generateAccess} disabled={generating || !client.email} className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-sm font-semibold mx-auto disabled:opacity-50 transition-all">
              <Key size={14} /> {generating ? 'Generating...' : 'Generate Portal Access'}
            </button>
            {!client.email && <p className="text-xs text-red-500 mt-2">Client needs an email address to use the portal.</p>}
          </div>
        )}
      </div>

      {/* Properties & Appraisals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E2E2DF] rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800 font-[Manrope] flex items-center gap-2"><Home size={18} className="text-[#4A6FA5]" />Properties</h3>
            <div className="flex items-center gap-2">
              {/* Iter179 — inline "Add Property" affordance so the
                  operator can grow a client's property list without
                  going via the New Appraisal modal. */}
              {!showAddProperty && (
                <button
                  data-testid="add-property-inline-btn"
                  onClick={() => setShowAddProperty(true)}
                  className="flex items-center gap-1 text-xs font-semibold text-[#D35400] border border-[#D35400] px-2 py-1 rounded-md hover:bg-[#FFF7ED]"
                >
                  <Plus size={12} strokeWidth={2.5} /> Add Property
                </button>
              )}
              <button onClick={() => navigate(`/properties?client_id=${id}`)} className="text-xs text-[#D35400] font-semibold hover:text-[#A04000]">View All</button>
            </div>
          </div>
          {showAddProperty && (
            <div data-testid="add-property-inline-form" className="mb-3 border border-[#D35400] bg-[#FFF7ED]/40 rounded-md p-3 space-y-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#A04000]">New property for {client?.name_first}</span>
                <button type="button" onClick={() => { setShowAddProperty(false); setAddPropForm({ property_address_num: '', property_address_street: '', property_address_suburb: '', property_address_city: '' }); }} className="text-[11px] text-slate-500 hover:text-slate-700 font-medium">Cancel</button>
              </div>
              {/* Iter182++ — "Use Client Address" one-click copy. Most
                  property records start at the same address the client
                  already lives at (owner-occupiers), so copying the four
                  address fields verbatim saves the operator a full re-type.
                  Disabled when the client has no street recorded. */}
              {(client?.address_street || client?.address_num || client?.address_suburb || client?.address_city) && (
                <button
                  type="button"
                  data-testid="use-client-address-btn"
                  onClick={() => setAddPropForm({
                    property_address_num:    client.address_num    || '',
                    property_address_street: client.address_street || '',
                    property_address_suburb: client.address_suburb || '',
                    property_address_city:   client.address_city   || '',
                  })}
                  title={`Copy: ${client.address_num || ''} ${client.address_street || ''}, ${client.address_suburb || ''}${client.address_suburb && client.address_city ? ', ' : ''}${client.address_city || ''}`.trim()}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 border border-dashed border-[#D35400] text-[#A04000] hover:bg-[#FFF7ED] text-[11px] font-semibold rounded transition"
                >
                  <MapPin size={11} strokeWidth={2.5} /> Use Client Address
                  <span className="text-[10px] font-normal text-slate-500 truncate">
                    · {client.address_num ? `${client.address_num} ` : ''}{client.address_street}{client.address_suburb ? `, ${client.address_suburb}` : ''}
                  </span>
                </button>
              )}
              <div className="grid grid-cols-[100px_1fr] gap-2">
                <input data-testid="add-prop-num" placeholder="No." value={addPropForm.property_address_num} onChange={e => setAddPropForm(p => ({ ...p, property_address_num: e.target.value }))} className="px-2.5 py-1.5 border border-[#E2E2DF] rounded text-sm" />
                <input data-testid="add-prop-street" placeholder="Street *" value={addPropForm.property_address_street} onChange={e => setAddPropForm(p => ({ ...p, property_address_street: e.target.value }))} className="px-2.5 py-1.5 border border-[#E2E2DF] rounded text-sm" />
              </div>
              <Combobox
                value={addPropForm.property_address_suburb || ''}
                onChange={v => setAddPropForm(p => {
                  const meta = suburbMeta.find(m => m.name === v);
                  return { ...p, property_address_suburb: v, property_address_city: (meta?.city && !p.property_address_city) ? meta.city : p.property_address_city };
                })}
                options={addPropForm.property_address_city ? [...suburbMeta.filter(m => m.city === addPropForm.property_address_city).map(m => m.name), ...suburbOptions.filter(s => !suburbMeta.find(m => m.name === s && m.city === addPropForm.property_address_city))] : suburbOptions}
                placeholder="Suburb (e.g. Dinsdale)"
                testId="add-prop-suburb-combobox"
                hint={(opt) => suburbMeta.find(x => x.name === opt)?.city || null}
              />
              <Combobox
                value={addPropForm.property_address_city || ''}
                onChange={v => setAddPropForm(p => ({ ...p, property_address_city: v }))}
                options={cityOptions}
                placeholder="City (e.g. Hamilton)"
                testId="add-prop-city-combobox"
              />
              <div className="flex justify-end items-center gap-2">
                {/* Iter202 — live duplicate-on-file hint. Surfaces a
                    soft warning the instant the address matches an
                    existing property on this client, BEFORE the
                    operator hits Save. */}
                {(() => {
                  const hit = findExistingPropertyMatch(addPropForm);
                  if (!hit) return null;
                  return (
                    <span data-testid="add-prop-dupe-inline-hint" className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-300 text-amber-800 mr-auto">
                      <AlertTriangle size={10} /> Already on file{hit.access_id ? ` as P${hit.access_id}` : ''}
                    </span>
                  );
                })()}
                <button data-testid="save-add-property" type="button" disabled={!addPropForm.property_address_street.trim() || savingAddProp} onClick={saveAddProperty} className="px-3 py-1.5 bg-[#D35400] text-white text-xs font-semibold rounded hover:bg-[#A04000] disabled:opacity-50">{savingAddProp ? 'Saving…' : 'Save property'}</button>
              </div>
            </div>
          )}
          {(client.properties || []).length === 0 ? <p className="text-sm text-slate-400 py-4">No properties yet</p> : (
            <div className="space-y-3">
              {client.properties.map(p => (
                <div key={p._id} className="p-3 border border-[#E2E2DF] rounded-md hover:bg-slate-50 transition-colors group">
                  <div className="flex items-center justify-between gap-3">
                    <div className="cursor-pointer flex-1 min-w-0" onClick={() => navigate(`/properties/${p._id}`)}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-slate-800 truncate">{p.property_address_num} {p.property_address_street}</p>
                        {p.property_no && <IdChip type="property" value={p.property_no} size="sm" testId={`client-prop-chip-${p._id}`} />}
                        {p.is_rental && <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#A04000] bg-[#FFF7ED] border border-[#D35400]/30 rounded-full px-2 py-0.5">Rental</span>}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">{p.property_address_suburb}{p.property_address_suburb && p.property_address_city ? ', ' : ''}{p.property_address_city}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {/* Three section-scoped pencils — saves a click + scroll for tenant/agent edits. */}
                      <button
                        data-testid={`edit-property-details-${p._id}`}
                        title="Edit property details"
                        onClick={() => navigate(`/properties/${p._id}?edit=property`)}
                        className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 hover:text-[#D35400] border border-transparent hover:border-[#D35400] rounded px-1.5 py-1 opacity-0 group-hover:opacity-100 transition"
                      >
                        <Edit2 size={11} /> Property
                      </button>
                      <button
                        data-testid={`edit-property-tenant-${p._id}`}
                        title={p.is_rental ? 'Edit tenant info' : 'Mark as rental & add tenant info'}
                        onClick={() => navigate(`/properties/${p._id}?edit=tenant`)}
                        className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 hover:text-[#D35400] border border-transparent hover:border-[#D35400] rounded px-1.5 py-1 opacity-0 group-hover:opacity-100 transition"
                      >
                        <Edit2 size={11} /> Tenant
                      </button>
                      {(p.is_rental || p.real_estate_agent_id || p.agent_name) && (
                        <button
                          data-testid={`edit-property-agent-${p._id}`}
                          title="Edit managing agent"
                          onClick={() => navigate(`/properties/${p._id}?edit=agent`)}
                          className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 hover:text-[#D35400] border border-transparent hover:border-[#D35400] rounded px-1.5 py-1 opacity-0 group-hover:opacity-100 transition"
                        >
                          <Edit2 size={11} /> Agent
                        </button>
                      )}
                      <button
                        data-testid={`add-roof-diagram-${p._id}`}
                        title={(p.roof_screenshot_photo_id || p.roof_diagram_photo_id) ? 'Change roof screenshot' : 'Add roof screenshot'}
                        onClick={() => setRoofDiagramPropId(p._id)}
                        className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.1em] border rounded px-1.5 py-1 opacity-0 group-hover:opacity-100 transition ${(p.roof_screenshot_photo_id || p.roof_diagram_photo_id) ? 'text-emerald-600 border-emerald-300 hover:bg-emerald-50' : 'text-slate-500 border-transparent hover:border-[#D35400] hover:text-[#D35400]'}`}
                      >
                        <ImageIcon size={11} /> {(p.roof_screenshot_photo_id || p.roof_diagram_photo_id) ? 'Screenshot' : 'Add Screenshot'}
                      </button>
                      {/* Iter178 — open the unified soft/hard delete modal. */}
                      <button
                        data-testid={`delete-property-${p._id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDelTarget({
                            kind: 'property',
                            id: p._id,
                            label: `${p.property_address_num || ''} ${p.property_address_street || ''}`.trim() || `Property ${p.property_no || p._id.slice(-6)}`,
                          });
                        }}
                        title="Delete this property (soft or hard)"
                        className="p-1 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity ml-1"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-[#E2E2DF] rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800 font-[Manrope] flex items-center gap-2"><ClipboardCheck size={18} className="text-[#D35400]" />Appraisals</h3>
            <div className="flex gap-2">
              <AppraisalStartButtons api={api} clientId={id} properties={client.properties || []}
                preferred={Array.from(new Set((client.properties || []).flatMap(p => p.preferred_quote_types || [])))}
                compact testIdPrefix="client" onAddProperty={() => setShowAddProperty(true)} />
              <button onClick={() => navigate(`/appraisals?client_id=${id}`)} className="text-xs text-[#D35400] font-semibold hover:text-[#A04000]">View All</button>
            </div>
          </div>
          {(client.appraisals || []).length === 0 ? <p className="text-sm text-slate-400 py-4">No appraisals yet</p> : (
            <div className="space-y-4">
              {/* Group appraisals by property */}
              {(() => {
                const propMap = {};
                (client.properties || []).forEach(p => { propMap[p._id] = `${p.property_address_num} ${p.property_address_street}, ${p.property_address_suburb}`; });
                const grouped = {};
                (client.appraisals || []).forEach(a => {
                  const key = a.property_id || 'unlinked';
                  if (!grouped[key]) grouped[key] = [];
                  grouped[key].push(a);
                });
                return Object.entries(grouped).map(([propId, apps]) => (
                  <div key={propId}>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1">
                      <MapPin size={11} /> {propMap[propId] || 'No Property'}
                    </p>
                    <div className="space-y-1.5">
                      {apps.map(a => (
                        <div key={a._id} className="flex items-center justify-between p-2.5 border border-[#E2E2DF] rounded-md hover:bg-slate-50 transition-colors group">
                          <div className="cursor-pointer flex-1" onClick={() => navigate(`/appraisals/${a._id}`)}>
                            <span className="text-sm font-medium text-slate-800">{a.quotation_type || a.quote_type_ref || 'Appraisal'}</span>
                            {a.appraisal_date && <span className="text-xs text-slate-400 ml-2">{String(a.appraisal_date).slice(0, 10)}</span>}
                            {a.contract_amount > 0 && <span className="text-xs text-slate-500 ml-2">${Number(a.contract_amount).toLocaleString()}</span>}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${a.appraisal_status === 'Completed' ? 'bg-green-50 text-green-700' : a.appraisal_status === 'Accepted' ? 'bg-blue-50 text-blue-700' : a.appraisal_status === 'Quoted' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                              {a.appraisal_status}
                            </span>
                            <button onClick={() => navigate(`/appraisals/${a._id}`)} className="p-1 text-slate-400 hover:text-[#D35400] opacity-0 group-hover:opacity-100 transition-opacity"><Edit2 size={13} /></button>
                            {/* Iter177 — open the unified soft/hard delete modal. */}
                            <button
                              data-testid={`delete-app-${a._id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDelTarget({
                                  kind: 'appraisal',
                                  id: a._id,
                                  label: a.appraisal_no ? `A${a.appraisal_no}` : `${a.quotation_type || a.quote_type_ref || 'Appraisal'} ${a._id.slice(-6)}`,
                                });
                              }}
                              title="Delete this appraisal (soft or hard)"
                              className="p-1 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Client-level Attachments — portfolio docs, ID, contracts */}
      <div className="mt-6">
        <AttachmentsTab api={api} scope="client" scopeId={id} title="Client Attachments" />
      </div>


      {/* Edit Client modal */}
      <ClientFormModal
        open={showEditClient}
        onClose={() => setShowEditClient(false)}
        initial={client}
        title={`Edit ${client?.name_first || ''} ${client?.name_last || ''}`.trim()}
        onSaved={() => load()}
      />

      {/* Iter179 — Roof Screenshot picker (renamed from "Roof Diagram"). */}
      {roofDiagramPropId && (() => {
        const p = (client?.properties || []).find(x => x._id === roofDiagramPropId);
        const addr = p ? [p.property_address_num, p.property_address_street, p.property_address_suburb, p.property_address_city].filter(Boolean).join(' ') : '';
        return (
          <RoofDiagramPicker
            propertyId={roofDiagramPropId}
            selectedId={p?.roof_screenshot_photo_id || p?.roof_diagram_photo_id || ''}
            address={addr}
            onSelect={async (photoId) => {
              try {
                await api.patch(`/properties/${roofDiagramPropId}`, { roof_screenshot_photo_id: photoId });
                await load();
              } catch (e) { alert(e.response?.data?.detail || 'Failed to save screenshot'); }
            }}
            onClose={() => setRoofDiagramPropId(null)}
          />
        );
      })()}

      {/* Iter177/178 — universal Soft/Hard Delete modal for the
          client itself, owned properties, and owned appraisals. On a
          successful client delete we redirect to the list; for nested
          property/appraisal deletes we just reload the page. */}
      {delTarget && (
        <DeleteConfirmDialog
          kind={delTarget.kind}
          entityId={delTarget.id}
          label={delTarget.label}
          api={api}
          onClose={() => setDelTarget(null)}
          onDeleted={() => {
            const wasClient = delTarget.kind === 'client';
            setDelTarget(null);
            if (wasClient) navigate('/clients');
            else load();
          }}
        />
      )}

      {/* Iter202 — duplicate-property confirm. Triggered when the
          operator tries to save an Add-Property address that matches
          an existing property on this client. Operator can Cancel
          (keeps the form open, no changes), Open the existing
          property, or explicitly "Create anyway". */}
      {addPropDupe && (
        <div
          data-testid="add-prop-dupe-confirm-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setAddPropDupe(null); }}
        >
          <div className="bg-white rounded-lg w-full max-w-md p-5 space-y-3 shadow-2xl">
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-600" />
              <h3 className="font-bold text-slate-900 flex-1">Property already on file</h3>
              <button onClick={() => setAddPropDupe(null)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            <div className="space-y-2 text-sm text-slate-700">
              <p>
                A property with this exact address is already linked to <strong>{client?.name_first} {client?.name_last}</strong>:
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded p-2.5 text-[13px]">
                <div className="font-semibold text-slate-800">
                  {[addPropDupe.property_address_num, addPropDupe.property_address_street].filter(Boolean).join(' ')}
                </div>
                <div className="text-slate-600">
                  {[addPropDupe.property_address_suburb, addPropDupe.property_address_city].filter(Boolean).join(', ')}
                </div>
                {addPropDupe.access_id && (
                  <div className="font-mono text-[11px] text-amber-700 mt-1">P{addPropDupe.access_id}</div>
                )}
              </div>
              <p className="text-xs text-slate-500">
                Duplicate addresses are blocked. Use the existing property — a second roof on the same property goes under Roof Details.
              </p>
            </div>
            <div className="flex items-center gap-2 justify-end pt-1">
              <button
                data-testid="add-prop-dupe-cancel"
                onClick={() => setAddPropDupe(null)}
                className="px-3 py-1.5 text-sm font-semibold border border-slate-300 rounded hover:bg-slate-50"
              >Cancel</button>
              <button
                data-testid="add-prop-dupe-open-existing"
                onClick={() => { const id = addPropDupe._id; setAddPropDupe(null); navigate(`/properties/${id}`); }}
                className="px-3 py-1.5 text-sm font-semibold border border-amber-400 text-amber-800 bg-amber-50 rounded hover:bg-amber-100"
              >Open existing</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
